import React, { useEffect, useState } from 'react';
import { premiumServicesList, PremiumServiceItem, PremiumServiceKeyBenefit, PremiumServiceFAQ } from 'constants/premium-service-constants';
import styled from 'styled-components';
import Footer from 'components/footer';
import { SuccessButton } from 'components/button';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSharePremiumService, fetchShareUrlContentAction, resetSharePremiumService } from '../premium-services.actions';
import PremiumServicesSelector from '../premium-services.selector';
import { propsToJS } from '__utils/immutable-to-js';
import Accordion from 'components/accordion';
import TalkToExpert from 'components/talk-to-expert';
import download from "images/download.svg";
import infoIcon from "images/info-icon.svg";
import LargePopup from 'components/popup/large-popup';
import CreateLead from 'components/create-lead';
import { isMintproApp, isProUser, downloadFileMintpro, addAgentCode } from '__utils/common-utils';
import { hideSharePopupAction } from 'components/share/share.actions';
import useFlashToggler from 'hooks/use-flash-toggler';
import { COLOR } from 'constants/colors';
import UserSelector from 'views/user/user.selector';
import { getEventName, Mixpanel } from '__utils/mixpanel';
import { getAgentCode, getUserDetails } from '__utils/storage';
import { useMediaQuery } from 'react-responsive';
import { LayoutContext } from 'components/app-routes';
import { HeaderIcons } from 'constants/header-config';
import LoginSelector from 'views/login/login.selector';
import ShareButton from 'components/share-button';

const PremiumServiceDetailsIntro = styled.div`
    padding: 16px;
    background-color: ${COLOR.GREY_BG};
    .customImg {
        width: 100%;
        padding-bottom: 24px;
    }
`;
const PremiumServiceDetailsIntroTitle = styled.div`
    padding-bottom: 20px;
    font-size: 18px;
    font-weight: 500;
    line-height: 1.17;
`;
const PremiumServiceDetailsIntroDesc = styled.div`
    font-size: 14px;
    font-weight: normal;
    line-height: 1.29;
`;
const PremiumServiceDetailsServiceFee = styled.div`
    padding: 20px 0px 8px;
    .fee-title {
        font-size: 12px;
        color: ${COLOR.GREY};
    }
    .fee-amount {
        font-size: 17px;
        text-decoration: line-through;
    }
    .fee-free {
        color: ${COLOR.GREEN};
        margin-left: 10px;
        span {
            font-weight: bold;
            font-size: 17px;
        }
    }
`;
const PremiumServiceDetailsWhiteCard = styled.div`
    color: ${COLOR.GREY};
    padding-bottom:24px;
    &.mintBox{
        background: ${COLOR.WHITE};
        padding: 16px;
        margin-top: 16px;
    }
    + div {
        border-top: 1px solid lightgrey;
        padding-top:16px;
    }
    p {
        font-size: 16px;
        padding: 10px 0px;
        font-weight: 600;
        color: ${COLOR.BLACK};
        line-height: normal;
    }
`;
const PremiumServiceDetailsKeyBenefit = styled.div`
    padding: 10px 0px;
    display: flex;
    img {
        height: 24px;
    }
    div {
        padding-left: 20px;
        flex: 1;
    }
`;
const PremiumServiceDetailsListItem = styled.div`
    padding: 10px 0px 10px 18px;
    position: relative;
    :before{
        content: '';
        position: absolute;
        width: 6px;
        height: 6px;
        background-color: ${COLOR.GREEN};
        display: block;
        border-radius: 50%;
        top: 15px;
        left:2px;
    }
`;
const PremiumServiceDetailsDownloadButton = styled.a`
    color: ${COLOR.GREEN};
    padding-left: 6px;
    img{
        position: relative;
        // top: 6px;
        left: -6px;
    }
`;
const PremiumServiceDetailsDisclaimer = styled.div`
    color: ${COLOR.GREY};
    padding: 16px;
    font-size: 14px;
    margin: 0 0 6px 30px;
    border-top: none!important;
    img {
        position: absolute;
        left: 15px;
        margin-top: -3px;
    }
`;

const PremiumServiceDetails: React.FC<any> = (props: any) => {

    const premiumServiceItem: PremiumServiceItem = premiumServicesList.filter((p: PremiumServiceItem) => p.route === window.location.pathname.split('/')[3])[0];
    const dispatch = useDispatch();
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const flash = useFlashToggler();
    const { shared, shareContent } = propsToJS(useSelector(PremiumServicesSelector));
    const { isAgentVerified } = propsToJS(useSelector(LoginSelector));
    const { agentDetails } = propsToJS(useSelector(UserSelector));
    const [ showCreateLeadPopup, setShowCreateLeadPopup ] = useState<boolean>(false);
    const { layoutProps, SetLayoutProps } = React.useContext(LayoutContext);

    useEffect(() => {
        SetLayoutProps({
            config: {
                goBackCallback: window.history?.length < 2 ? () => window.location.href = window.location.origin + "/v1/premium-services" : undefined,
                title: premiumServiceItem.title,
                headerIconSet: HeaderIcons.cartNcall,
            },
            breadcrumbList: [ { text: 'Home', url: addAgentCode('v1/home'), isExternal: true }, { text: 'Premium Services', url: '/premium-services' }, { text: premiumServiceItem.title, url: '' } ]
        })
        // redirect to not found since premium services is not built for desktop
        if (!isMobile)
        {
            window.location.href = window.location.origin + '/v1/home?url=404';
        }
        // fetch share content
        dispatch(fetchShareUrlContentAction(premiumServiceItem.key));
    }, []);

    useEffect(() => {
        if (shared)
        {
            flash.success('Preimum service shared successfully');
            dispatch(resetSharePremiumService());
            dispatch(hideSharePopupAction());
        }
    }, [ shared, dispatch ]);

    const getShareURL = () => {
        return shareContent ? shareContent.fileUrl : null;
    }

    const getShareMessage = () => {
        return window.encodeURIComponent(`Dear customer, you can check out the details of the ${premiumServiceItem.name} on ${getShareURL()}. Do get in touch with me on ${agentDetails?.primaryMobile} to complete the purchase. - ${agentDetails?.name}`);
    }

    const sharePremiumService = (shareTo: string, type: string) => {
        let dataToSend = {
            type: type,
            value: shareTo,
            psName: premiumServiceItem.title,
            psDesc: premiumServiceItem.shareDesc,
            shareUrl: type === "mobile" ? window.encodeURIComponent(getShareURL()) : getShareURL(),
            imgUrl: premiumServiceItem.imgSrc,
        }
        dispatch(fetchSharePremiumService(dataToSend));
    }

    const onReportDownloadClick = (premiumServiceItem: PremiumServiceItem) => {
        downloadFileMintpro(premiumServiceItem.title, premiumServiceItem.downloadURL, "application/pdf")
        Mixpanel.track(getEventName("reportDownloaded", "clicked", "premiumService"), {
            agentCode: getAgentCode(),
            user: getUserDetails(),
            type: premiumServiceItem.route
        });
    };

    // redirection to account creation page of mintpro
    const goToMintproAccountVerification = () => {
        const mintproAccountUrl: string = process.env.REACT_APP_MINTPRO_ACCOUNT_URL ? process.env.REACT_APP_MINTPRO_ACCOUNT_URL : "";
        if (isMintproApp()) {
            var featurePayload = {
                type: 'close', 
                payload: {
                      screenUrl: 'https://app.mintpro.in/myaccount'
                },
            };
                window.postMessage(JSON.stringify(featurePayload), '*');
            }
        else{
            window.location.assign(mintproAccountUrl);
        }
        // window.location.assign(mintproAccountUrl);
        Mixpanel.track(getEventName("mintproAccountVerification" ,"clicked","cart-review"), {
            agentCode: getAgentCode(),
            user: getUserDetails()
        });
    }
    return (
        <div>
            {
                premiumServiceItem ? <div>
                    <PremiumServiceDetailsIntro>
                        {/* <iframe title={`${premiumServiceItem.name}video`} width="100%" height="188" src="https://www.youtube.com/embed/owdW380ImU4"
                            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen>
                        </iframe> */}
                        <img src={ `${premiumServiceItem.imgSrc}` } alt={ premiumServiceItem.textAlt } className="customImg" />
                        <PremiumServiceDetailsIntroTitle>{ premiumServiceItem.title }</PremiumServiceDetailsIntroTitle>
                        <PremiumServiceDetailsIntroDesc>{ premiumServiceItem.longDescription }</PremiumServiceDetailsIntroDesc>
                        <PremiumServiceDetailsServiceFee>
                            <span className="fee-title">Service Fee</span><br />
                            <span className="fee-amount">&#8377; { premiumServiceItem.serviceFee }</span>
                            <span className="fee-free"><span>Free</span> (Limited time)</span>
                        </PremiumServiceDetailsServiceFee>
                    </PremiumServiceDetailsIntro>

                    <div className="p16">
                        <PremiumServiceDetailsWhiteCard>
                            <p>Key benefits of this Solution</p>
                            { premiumServiceItem.keyBenefits.map((benefit: PremiumServiceKeyBenefit, i: number) => <PremiumServiceDetailsKeyBenefit key={ i }>
                                <img src={ `../v1/img/${benefit.icon}` } alt="search" />
                                <div>{ benefit.text }</div>
                            </PremiumServiceDetailsKeyBenefit>) }
                        </PremiumServiceDetailsWhiteCard>
                        <PremiumServiceDetailsWhiteCard>
                            <p>Who is this for</p>
                            { premiumServiceItem.whoIsThisFor.map((content: string, i: number) => <PremiumServiceDetailsListItem key={ i }>
                                { content }
                            </PremiumServiceDetailsListItem>) }
                        </PremiumServiceDetailsWhiteCard>
                        { premiumServiceItem.topFeatures ? <PremiumServiceDetailsWhiteCard>
                            <p>Top features of this Solution</p>
                            { premiumServiceItem.topFeatures.map((content: string, i: number) => <PremiumServiceDetailsListItem key={ i } dangerouslySetInnerHTML={ { __html: content } }></PremiumServiceDetailsListItem>) }
                            <PremiumServiceDetailsDownloadButton href={ premiumServiceItem.downloadURL } target="_blank" onClick={ () => onReportDownloadClick(premiumServiceItem) }>
                                <img src={ download } alt="download" />
                                <span>Download Detailed Solution Brochure</span>
                            </PremiumServiceDetailsDownloadButton>
                        </PremiumServiceDetailsWhiteCard> : null }

                        { premiumServiceItem.whatYouGet ? <PremiumServiceDetailsWhiteCard>
                            <p>What you get</p>
                            <div>{ premiumServiceItem.whatYouGet.desc }</div>
                            { premiumServiceItem.whatYouGet.points.map((content: string, i: number) => <PremiumServiceDetailsListItem key={ i }>
                                { content }
                            </PremiumServiceDetailsListItem>) }
                            <PremiumServiceDetailsDownloadButton href={ premiumServiceItem.downloadURL } target="_blank" onClick={ () => onReportDownloadClick(premiumServiceItem) }>
                                <img src={ download } alt="download" />
                                <span>Download Detailed Solution Brochure</span>
                            </PremiumServiceDetailsDownloadButton>
                        </PremiumServiceDetailsWhiteCard> : null }

                    </div>


                    <TalkToExpert title={ `Want to know more about this premium Solution?` }></TalkToExpert>
                    <PremiumServiceDetailsWhiteCard className="mintBox">
                        <p>Frequently asked questions</p>
                        <Accordion list={ premiumServiceItem.faqs.map((faq: PremiumServiceFAQ, i: number) => { return { title: faq.question, content: faq.answer } }) }></Accordion>
                    </PremiumServiceDetailsWhiteCard>
                    { premiumServiceItem.disclaimer ? <PremiumServiceDetailsDisclaimer>
                        <img src={ infoIcon } alt="disclaimer icon" />
                        { premiumServiceItem.disclaimer }</PremiumServiceDetailsDisclaimer> : null }
                </div> : null
            }
            {isProUser() ?
            <Footer>
                { isAgentVerified === "not_verified" ? <p className="6">Please complete your profile verification to proceed with the transaction</p> : null }
                <div style={{ display: 'flex' }}>
                    {shareContent && shareContent.fileUrl ? <ShareButton shareConfig={ {message: getShareMessage(), shareUrl: getShareURL(), onEmailOrSMSShare: sharePremiumService, shareType:`sharePremiumService`,
                        mixpanelData: { eventName: 'premiumServiceShare', source: 'premiumService'} }}/> : null}
                    { isAgentVerified === "verified" ? 
                        <SuccessButton style={{ flex: 1 }} onClick={() => setShowCreateLeadPopup(true)} >Create Lead</SuccessButton> : 
                        <SuccessButton style={{ flex: 1 }} onClick={() => goToMintproAccountVerification()} >Complete Account Verification</SuccessButton>
                    }
                </div>
            </Footer> 
            : null}
            {showCreateLeadPopup ? <LargePopup title={`Create Lead`} onClose={() => setShowCreateLeadPopup(false)} showBacktoMintPro><CreateLead type={premiumServiceItem.tag} /></LargePopup> : null}
        </div>
    )
};

export default PremiumServiceDetails;